"use client";

import { useTranslations } from "next-intl";
import type { BlogCategory } from "@/lib/blog/labels";
import { splitTags } from "@/lib/blog/tags";
import { cn } from "@/lib/utils";
import { BlogStatusBadge } from "./BlogStatusBadge";

const NEUTRAL_CHIP =
  "rounded-lg bg-bg-tertiary px-3 py-1.5 text-xs font-medium text-text-primary";

interface BlogBadgesProps {
  category: BlogCategory;
  statementStatus:
    | "draft"
    | "under_voting"
    | "approved"
    | "rejected"
    | "executed"
    | null
    | undefined;
  tags: string | null | undefined;
  className?: string;
}

/**
 * Badge cluster shared by the blog list card + detail header so the two always
 * match. Normal blogs read [category][first tag] in the neutral chip colour; DAO
 * statements/actions read [category][coloured status]. The executed vote-count
 * badge («N رأی») is a follow-up — it needs a vote source (see the DAO↔blog
 * integration handoff doc).
 */
export const BlogBadges: React.FC<BlogBadgesProps> = ({
  category,
  statementStatus,
  tags,
  className,
}) => {
  const t = useTranslations("blog");
  const categoryLabel = t(`category.${category as BlogCategory}`);
  const firstTag = splitTags(tags)[0];

  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      {categoryLabel && <span className={NEUTRAL_CHIP}>{categoryLabel}</span>}
      {statementStatus ? (
        <BlogStatusBadge status={statementStatus} />
      ) : (
        firstTag && <span className={NEUTRAL_CHIP}>{firstTag}</span>
      )}
    </div>
  );
};
